import useLogout from '@Guidelines/Shell/AppShell/Hooks/useLogout';
import { theme } from '@Guidelines/Theme/theme';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { css } from '@emotion/react';
import { LogOut } from 'lucide-react';
import * as React from 'react';
import { Menu } from '@verclam/ui-kit';
import type { MenuHandle, MenuItem } from '@verclam/ui-kit';

const triggerStyle = css`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 2.25rem;
	height: 2.25rem;
	flex-shrink: 0;
	border: none;
	border-radius: ${theme.radii.pill};
	background-color: ${theme.appBar.avatarBadge.background};
	color: ${theme.appBar.avatarBadge.text};
	font-family: ${theme.fonts.display};
	font-weight: ${theme.fontWeights.semibold};
	font-size: ${theme.fontSizes.small};
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid ${theme.colors.accent};
		outline-offset: 2px;
	}
`;

/** Avatar-as-trigger opening a popup `Menu` with a single entry, « Se déconnecter ». */
const UserMenu: React.FC = () => {
	const handleLogout = useLogout();
	const { user } = useStrictAuthenticatedUser();
	const menuRef = React.useRef<MenuHandle>(null);

	const initials = React.useMemo(() => {
		return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
	}, [user]);

	const handleToggle = React.useCallback((event: React.SyntheticEvent) => {
		menuRef.current?.toggle(event);
	}, []);

	const model = React.useMemo<readonly MenuItem[]>(
		() => [{ label: 'Se déconnecter', icon: <LogOut size={16} />, command: handleLogout }],
		[handleLogout],
	);

	return (
		<React.Fragment>
			<button type="button" css={triggerStyle} onClick={handleToggle} aria-label="Menu utilisateur">
				{initials}
			</button>
			<Menu ref={menuRef} popup model={model} ariaLabel="Menu utilisateur" popupAlignment="right" />
		</React.Fragment>
	);
};

export default UserMenu;
